import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Pagination,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SearchIcon from '@mui/icons-material/Search';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import { styled } from '@mui/material/styles';
import useCachedQuery from '../hooks/useCachedQuery';
import {
  fetchLeaderboard,
  queryKeys,
  STALE_TIMES,
} from '../api/queries';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { deepPurple, blue, green } from '@mui/material/colors';
import { resolveProfileImage } from '../common/utils';

export const trophyColors = [green[400], blue[400], deepPurple[400]];

export const TrophyIcon = styled(EmojiEventsIcon)(({ theme, color }) => ({
  color: color || theme.palette.secondary.main,
  fontSize: '30px',
}));

const ACCENT = '#137274';

const LeaderboardPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const loggedInUser = useSelector((state) => state.user.userInfo);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState(null);
  const usersPerPage = 10;
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 45%, #ffffff 100%)';

  const { data: leaderboardData, loading: leaderboardLoading } = useCachedQuery(
    queryKeys.leaderboard,
    fetchLeaderboard,
    { staleTime: STALE_TIMES.leaderboard },
  );

  useEffect(() => {
    if (!Array.isArray(leaderboardData)) return;
    setUsers(leaderboardData);
    const rank = leaderboardData.findIndex((user) => user.id === loggedInUser?._id) + 1;
    setUserRank(rank > 0 ? rank : null);
    setLoading(false);
  }, [leaderboardData, loggedInUser?._id]);

  useEffect(() => {
    if (leaderboardLoading && !leaderboardData) setLoading(true);
  }, [leaderboardLoading, leaderboardData]);

  const handleChangePage = (_event, value) => {
    setPage(value);
  };

  const handleUserClick = (username) => {
    if (username) navigate(`/user/${username}`);
  };

  const filteredUsers = useMemo(
    () =>
      users.filter((user) => {
        const q = searchTerm.toLowerCase();
        return (
          (user.name || '').toLowerCase().includes(q) ||
          (user.username || '').toLowerCase().includes(q)
        );
      }),
    [users, searchTerm],
  );

  const myEntry = useMemo(() => {
    if (!loggedInUser?._id) return null;
    return users.find((user) => user.id === loggedInUser._id) || null;
  }, [users, loggedInUser?._id]);

  const startIndex = (page - 1) * usersPerPage;
  const selectedUsers = filteredUsers.slice(startIndex, startIndex + usersPerPage);
  const topThree = !searchTerm ? users.slice(0, 3) : [];
  const topPoints = users[0]?.points ?? 0;
  const pageCount = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));

  const podiumOrder = topThree.length === 3 ? [topThree[1], topThree[0], topThree[2]] : topThree;
  const podiumRanks = topThree.length === 3 ? [2, 1, 3] : topThree.map((_, i) => i + 1);
  const podiumHeights = topThree.length === 3 ? [120, 150, 100] : [140, 120, 100];

  const renderRankRow = (user, rank, { pinned = false } = {}) => {
    const isYou = loggedInUser?._id && user.id === loggedInUser._id;
    const isTop = rank <= 3 && !searchTerm && !pinned;

    return (
      <Box
        key={`${pinned ? 'pinned-' : ''}${user.id || user.username || rank}`}
        onClick={() => handleUserClick(user.username)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1.2,
          borderRadius: 1.75,
          border: `1px solid ${isYou ? alpha(ACCENT, 0.45) : border}`,
          bgcolor: isYou
            ? alpha(ACCENT, isDark ? 0.14 : 0.06)
            : isDark
              ? alpha('#fff', 0.02)
              : alpha(ACCENT, 0.02),
          cursor: 'pointer',
          transition: 'border-color 0.15s, background 0.15s',
          '&:hover': {
            borderColor: alpha(ACCENT, 0.4),
            bgcolor: isDark ? alpha('#fff', 0.05) : alpha(ACCENT, 0.05),
          },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontWeight: 800,
            fontSize: '0.8rem',
            bgcolor: isTop
              ? alpha(trophyColors[rank - 1], 0.2)
              : isDark
                ? alpha('#fff', 0.08)
                : alpha('#000', 0.05),
            color: isTop ? trophyColors[rank - 1] : muted,
          }}
        >
          {isTop ? <EmojiEventsIcon sx={{ fontSize: 18 }} /> : rank}
        </Box>

        <Avatar
          src={resolveProfileImage(user.profileImage)}
          sx={{
            width: 36,
            height: 36,
            fontSize: '0.85rem',
            fontWeight: 700,
            bgcolor: alpha(ACCENT, 0.15),
            color: ACCENT,
          }}
        >
          {(user.name?.[0] || user.username?.[0] || '?').toUpperCase()}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              sx={{
                fontWeight: isTop || isYou ? 700 : 600,
                fontSize: '0.9rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.name || user.username}
            </Typography>
            {isYou && (
              <Chip
                label={pinned ? 'Your rank' : 'You'}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  bgcolor: ACCENT,
                  color: '#fff',
                }}
              />
            )}
          </Box>
          {user.username && (
            <Typography variant="caption" sx={{ color: muted }}>
              @{user.username}
            </Typography>
          )}
        </Box>

        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '0.9rem',
            color: ACCENT,
            flexShrink: 0,
          }}
        >
          {user.points}
          <Typography
            component="span"
            variant="caption"
            sx={{ color: muted, ml: 0.5, fontWeight: 600 }}
          >
            pts
          </Typography>
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg, px: { xs: 1.5, sm: 2.5 }, py: { xs: 2.5, md: 3.5 } }}>
      <Helmet>
        <title>Leaderboard | Track your coding progress | CrafterHack</title>
        <meta
          name="description"
          content="This is the page for tracking your progress on solved coding questions"
        />
      </Helmet>

      <Box sx={{ maxWidth: 920, mx: 'auto' }}>
        {/* Hero */}
        <Box
          sx={{
            mb: 2.5,
            p: { xs: 2, sm: 2.75 },
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            background: isDark
              ? `linear-gradient(135deg, ${alpha(ACCENT, 0.2)} 0%, #161b22 55%, ${alpha('#ff9800', 0.08)} 100%)`
              : `linear-gradient(135deg, ${alpha(ACCENT, 0.1)} 0%, #fff 55%, ${alpha('#ff9800', 0.08)} 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#ff9800', isDark ? 0.2 : 0.15),
                flexShrink: 0,
              }}
            >
              <EmojiEventsIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.35rem', sm: '1.6rem' },
                  letterSpacing: '-0.03em',
                  mb: 0.5,
                }}
              >
                Leaderboard
              </Typography>
              <Typography variant="body2" sx={{ color: muted, maxWidth: 520, lineHeight: 1.5 }}>
                Climb the ranks by solving problems. Consistency compounds — every Accepted submission
                moves you up.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
            <Chip
              icon={<GroupsIcon sx={{ fontSize: '16px !important' }} />}
              label={`${users.length} solvers`}
              sx={{
                fontWeight: 600,
                bgcolor: isDark ? alpha('#fff', 0.06) : alpha(ACCENT, 0.08),
                color: isDark ? '#e6edf3' : '#0f2a2b',
                '& .MuiChip-icon': { color: ACCENT },
              }}
            />
            <Chip
              icon={<TrendingUpIcon sx={{ fontSize: '16px !important' }} />}
              label={`Top score ${topPoints}`}
              sx={{
                fontWeight: 600,
                bgcolor: isDark ? alpha('#fff', 0.06) : alpha(ACCENT, 0.08),
                color: isDark ? '#e6edf3' : '#0f2a2b',
                '& .MuiChip-icon': { color: ACCENT },
              }}
            />
            {userRank != null && (
              <Chip
                icon={<LocalFireDepartmentIcon sx={{ fontSize: '16px !important' }} />}
                label={`Your rank #${userRank}`}
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha('#f50057', isDark ? 0.2 : 0.1),
                  color: '#f50057',
                  '& .MuiChip-icon': { color: '#f50057' },
                }}
              />
            )}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
            <CircularProgress sx={{ color: ACCENT }} />
          </Box>
        ) : (
          <>
            {/* Podium */}
            {topThree.length > 0 && (
              <Box
                sx={{
                  mb: 3,
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 2.5,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.02em', mb: 2 }}>
                  Top solvers
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    gap: { xs: 1, sm: 2 },
                    minHeight: 200,
                  }}
                >
                  {podiumOrder.map((user, i) => {
                    const rank = podiumRanks[i];
                    const height = podiumHeights[i] || 110;
                    const color = trophyColors[rank - 1] || ACCENT;
                    return (
                      <Box
                        key={user.id || user.username || rank}
                        onClick={() => handleUserClick(user.username)}
                        sx={{
                          flex: 1,
                          maxWidth: 180,
                          textAlign: 'center',
                          cursor: 'pointer',
                          '&:hover .podium-bar': {
                            filter: 'brightness(1.05)',
                          },
                        }}
                      >
                        <Avatar
                          src={resolveProfileImage(user.profileImage)}
                          sx={{
                            width: rank === 1 ? 56 : 44,
                            height: rank === 1 ? 56 : 44,
                            mx: 'auto',
                            mb: 1,
                            fontWeight: 800,
                            bgcolor: alpha(color, 0.2),
                            color,
                            border: `2px solid ${color}`,
                          }}
                        >
                          {(user.name?.[0] || user.username?.[0] || '?').toUpperCase()}
                        </Avatar>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            mb: 0.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            px: 0.5,
                          }}
                        >
                          {user.name || user.username}
                        </Typography>
                        <Typography variant="caption" sx={{ color: muted, display: 'block', mb: 1 }}>
                          {user.points} pts
                        </Typography>
                        <Box
                          className="podium-bar"
                          sx={{
                            height,
                            borderRadius: '10px 10px 4px 4px',
                            bgcolor: alpha(color, isDark ? 0.28 : 0.18),
                            border: `1px solid ${alpha(color, 0.45)}`,
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            pt: 1.25,
                            transition: 'filter 0.2s',
                          }}
                        >
                          <Typography sx={{ fontWeight: 900, fontSize: '1.25rem', color }}>
                            #{rank}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Search + list */}
            <Box
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2.5,
                border: `1px solid ${border}`,
                bgcolor: surface,
                mb: 2.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.02em' }}>
                  All rankings
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search name or username…"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: muted }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: '100%', sm: 260 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.03),
                    },
                  }}
                />
              </Box>

              {isLoggedIn && myEntry && userRank != null && (
                <Box sx={{ mb: 1.5 }}>
                  {renderRankRow(myEntry, userRank, { pinned: true })}
                  <Box sx={{ height: '1px', bgcolor: border, mt: 1.5 }} />
                </Box>
              )}

              {selectedUsers.length === 0 ? (
                <Typography sx={{ color: muted, textAlign: 'center', py: 4 }}>
                  No solvers match your search.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedUsers.map((user) => {
                    const rank = users.indexOf(user) + 1;
                    return renderRankRow(user, rank);
                  })}
                </Box>
              )}

              {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={handleChangePage}
                    color="primary"
                    sx={{
                      '& .MuiPaginationItem-root.Mui-selected': {
                        bgcolor: ACCENT,
                        '&:hover': { bgcolor: '#0f5c5e' },
                      },
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Creative CTA strip */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                border: `1px solid ${border}`,
                bgcolor: surface,
                background: isDark
                  ? `linear-gradient(120deg, ${alpha(ACCENT, 0.15)}, ${alpha('#f50057', 0.08)})`
                  : `linear-gradient(120deg, ${alpha(ACCENT, 0.08)}, ${alpha('#f50057', 0.05)})`,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 800, mb: 0.5, letterSpacing: '-0.02em' }}>
                  Want to climb faster?
                </Typography>
                <Typography variant="body2" sx={{ color: muted, maxWidth: 420 }}>
                  Solve daily problems, join contests, or take on the 75 Hard challenge to stack points.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  component={Link}
                  to="/problems"
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    bgcolor: ACCENT,
                    '&:hover': { bgcolor: '#0f5c5e' },
                  }}
                >
                  Practice
                </Button>
                <Button
                  component={Link}
                  to="/challenges/75-hard-coding-challenge"
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: border,
                    color: isDark ? '#e6edf3' : '#0f2a2b',
                  }}
                >
                  75 Hard
                </Button>
              </Box>
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
};

export default LeaderboardPage;
